import fs from 'node:fs'
import path from 'node:path'

export const DEFAULT_DEV_PORT = 4242

const DEV_PORT_FLAG_PATTERN = /(?:^|\s)(?:--port(?:=|\s+)|-p(?:=|\s+)?)(\d+)(?=\s|$)/
const DEV_PORT_ENV_PATTERN = /(?:^|\s)PORT=(\d+)(?=\s|$)/

export interface DevScriptPortResult {
  status: 'updated' | 'preserved' | 'skipped' | 'missing-package' | 'missing-script'
  port: number
}

interface PackageJsonShape {
  scripts?: Record<string, string>
}

export function detectDevPort(cwd: string): number {
  const pkg = readPackageJson(cwd)
  if (!pkg) return DEFAULT_DEV_PORT

  return detectDevPortFromScript(pkg.scripts?.dev) ?? DEFAULT_DEV_PORT
}

export function detectDevPortFromScript(devScript?: string): number | undefined {
  if (typeof devScript !== 'string') return undefined

  const trimmed = devScript.trim()
  if (trimmed.length === 0) return undefined

  const flagMatch = trimmed.match(DEV_PORT_FLAG_PATTERN)
  const envMatch = trimmed.match(DEV_PORT_ENV_PATTERN)
  const rawPort = flagMatch?.[1] ?? envMatch?.[1]
  if (!rawPort) return undefined

  const port = Number.parseInt(rawPort, 10)
  return isValidPort(port) ? port : undefined
}

export function ensureDefaultDevScriptPort(cwd: string): DevScriptPortResult {
  const pkgPath = path.join(cwd, 'package.json')
  if (!fs.existsSync(pkgPath)) {
    return { status: 'missing-package', port: DEFAULT_DEV_PORT }
  }

  const raw = fs.readFileSync(pkgPath, 'utf-8')
  let pkg: PackageJsonShape

  try {
    pkg = JSON.parse(raw) as PackageJsonShape
  } catch {
    return { status: 'missing-package', port: DEFAULT_DEV_PORT }
  }

  const devScript = pkg.scripts?.dev
  if (typeof devScript !== 'string' || devScript.trim().length === 0) {
    return { status: 'missing-script', port: DEFAULT_DEV_PORT }
  }

  const explicitPort = detectDevPortFromScript(devScript)
  if (explicitPort) {
    return { status: 'preserved', port: explicitPort }
  }

  if (!isPlainNextDevScript(devScript)) {
    return { status: 'skipped', port: DEFAULT_DEV_PORT }
  }

  const normalizedScript = `${normalizeWhitespace(devScript)} --port ${DEFAULT_DEV_PORT}`
  pkg.scripts = {
    ...pkg.scripts,
    dev: normalizedScript
  }

  const newline = raw.includes('\r\n') ? '\r\n' : '\n'
  const indent = detectIndent(raw)
  fs.writeFileSync(pkgPath, `${JSON.stringify(pkg, null, indent)}${newline}`, 'utf-8')

  return { status: 'updated', port: DEFAULT_DEV_PORT }
}

function readPackageJson(cwd: string): PackageJsonShape | undefined {
  const pkgPath = path.join(cwd, 'package.json')
  if (!fs.existsSync(pkgPath)) return undefined

  try {
    return JSON.parse(fs.readFileSync(pkgPath, 'utf-8')) as PackageJsonShape
  } catch {
    return undefined
  }
}

function isPlainNextDevScript(devScript: string): boolean {
  const normalized = normalizeWhitespace(devScript)
  return (
    normalized === 'next dev' ||
    normalized === 'next dev --turbopack' ||
    normalized === 'next dev --turbo'
  )
}

function normalizeWhitespace(value: string): string {
  return value.trim().replace(/\s+/g, ' ')
}

function detectIndent(raw: string): string | number {
  const indentMatch = raw.match(/^[ \t]+(?=")/m)
  return indentMatch?.[0] ?? 2
}

function isValidPort(port: number): boolean {
  return Number.isInteger(port) && port > 0 && port <= 65535
}
